import type { Recurrence } from 'api/recurrence_definitions/endpoints/types';
import { fetchOccurrenceSeries } from '../endpoints/fetchOccurrenceSeries';
import { acceptDegraded } from 'validation';

export interface ComputeOccurrenceSeriesArgs {
  /** The first run; the instance chain's immutable anchor. */
  readonly firstOccurrence: Date;
  /** Every rule in effect, sent in ONE request — see {@link fetchOccurrenceSeries}. */
  readonly recurrences: readonly Recurrence[];
  /** Instance-level scheduling window key ("zone|name"), or "" for none. */
  readonly schedulingWindow: string;
  /** IANA timezone the rules are evaluated in. */
  readonly timezone: string;
  /** How many following occurrences to ask for. */
  readonly count: number;
}

/**
 * The occurrences following `firstOccurrence`, as instants.
 *
 * This is the one place the occurrence series crosses between the wire's ISO
 * strings and the instants the UI reasons about, in BOTH directions: the anchor
 * goes out as `toISOString()` and the answers come back as `Date`. Taking the
 * anchor as a `Date` is what makes that boundary enforceable — an offset-free
 * wall-clock string cannot be passed here at all, so it cannot be reinterpreted
 * in the wrong zone on the way out.
 *
 * Unparseable entries are DROPPED rather than surfaced as `Invalid Date`. A
 * short series is already the endpoint's documented answer when the schedule
 * runs out, so a caller has to handle one regardless, and there is nothing
 * useful to render for a datetime we cannot place on a timeline.
 *
 * Failures propagate. Whether a lost series should blank the panel or degrade
 * to "no following occurrences" is a UI policy, so the caller owns it.
 */
export async function computeOccurrenceSeries({
  firstOccurrence,
  recurrences,
  schedulingWindow,
  timezone,
  count,
}: ComputeOccurrenceSeriesArgs): Promise<readonly Date[]> {
  // No rule means a one-off submit: there is no series to ask about, and the
  // backend answers `[]` for exactly this case. Skipping the round trip keeps
  // the common one-off path free of a request.
  if (recurrences.length === 0) {
    return [];
  }

  const { occurrences } = acceptDegraded(
    await fetchOccurrenceSeries({
      first_occurrence: firstOccurrence.toISOString(),
      timezone,
      recurrences,
      ...(schedulingWindow ? { scheduling_window: schedulingWindow } : {}),
      count,
    })
  );

  return occurrences.map((occurrence) => new Date(occurrence)).filter((instant) => !Number.isNaN(instant.getTime()));
}
